// What the corrections page should SAY when it has no finished correction to show.
//
// The page lists only corrections the teacher has closed (GET
// story-correction/closed-corrections). A learner who submitted an hour ago has
// an *open* correction, which that endpoint deliberately omits, so the page
// used to answer "No corrections found", which reads as "your work was lost".
// Pairing the closed list with the open one (GET
// story-correction/pending-corrections) lets us tell the two apart: nothing sent
// yet vs. sent and waiting.

/** One submission still in the teacher's queue. Shape of `answers[].data`. */
export interface PendingCorrection {
  storyId?: string
  createdAt?: number
  answer?: string
  recordingUrl?: string
}

/** The turnaround maayot guarantees on the Pro plan. */
export const CORRECTION_TURNAROUND_HOURS = 48

/**
 * Past this, repeating the guarantee is worse than admitting the delay. A day's
 * grace beyond the guarantee, not the guarantee itself: the clock starts when
 * the learner hit submit, not when the teacher's day did, so a correction that
 * lands a few hours late has not really broken the promise.
 */
export const CORRECTION_OVERDUE_HOURS = 72

const HOUR_MS = 60 * 60 * 1000

/** The oldest waiting submission: the one whose delay the learner feels. */
export const oldestPending = (pending: PendingCorrection[]): PendingCorrection | null => {
  const dated = pending.filter((p) => typeof p?.createdAt === 'number')
  if (!dated.length) return pending[0] ?? null
  return dated.reduce((oldest, p) => ((p.createdAt as number) < (oldest.createdAt as number) ? p : oldest))
}

/**
 * A submission is only "late" once we can prove it: rows written by older builds
 * carry no `createdAt`, and an undated submission must never be called overdue.
 */
export const isCorrectionOverdue = (correction: PendingCorrection | null, now: number = Date.now()): boolean =>
  typeof correction?.createdAt === 'number' && now - correction.createdAt > CORRECTION_OVERDUE_HOURS * HOUR_MS

export type CorrectionsViewKind =
  /** The plan no longer includes teacher corrections (a downgraded Pro, say). */
  | 'not-entitled'
  /** Nothing finished, nothing waiting: the learner has never sent work in. */
  | 'nothing-submitted'
  /** Nothing finished yet, but the teacher has their work. */
  | 'awaiting'
  /** Waiting longer than we promised. */
  | 'awaiting-overdue'
  /** The pending list could not be loaded, so we must not claim either way. */
  | 'unknown'
  /** There are finished corrections to list. */
  | 'has-corrections'

export interface CorrectionsViewInput {
  /** Corrections the teacher has closed: what the list renders. */
  closedCount: number
  /**
   * Whether the plan includes teacher corrections. A learner who downgraded from
   * Pro keeps the link (and their history), so telling them to go write for a
   * teacher would promise something their plan no longer buys.
   */
  isEntitled: boolean
  /** Submissions still open. Empty when none, ignored when `pendingKnown` is false. */
  pending: PendingCorrection[]
  /**
   * False when the pending request failed (offline, or a frontend deployed ahead
   * of the backend). We then know only that the list is empty, not why.
   */
  pendingKnown: boolean
  now?: number
}

/**
 * The single decision the page makes. Kept apart from the view so the wording of
 * each state is pinned by tests rather than by reading JSX.
 */
export const resolveCorrectionsView = ({
  closedCount,
  isEntitled,
  pending,
  pendingKnown,
  now = Date.now(),
}: CorrectionsViewInput): CorrectionsViewKind => {
  // Past corrections stay readable whatever the plan says today.
  if (closedCount > 0) return 'has-corrections'
  // Work already with the teacher is coming back whatever the plan says: the free correction
  // is sent from Free and Standard, and a Pro who downgrades keeps what they sent.
  if (pendingKnown && pending.length) {
    return isCorrectionOverdue(oldestPending(pending), now) ? 'awaiting-overdue' : 'awaiting'
  }
  if (!isEntitled) return 'not-entitled'
  if (!pendingKnown) return 'unknown'
  return 'nothing-submitted'
}
